import { useCallback, useEffect, useRef, useState } from 'react';
import type { PicksFilterValue } from '@/utils/ballotPicks';

// Listed in `PERSISTENT_KEYS` in storageUtils, so it survives the `beforeunload`
// wipe on refresh but is cleared by `clearVoteMateStorageOnLogout` on sign-out.
const STORAGE_KEY = 'votemate_election_filters';

/** Keyed by election identity: one master switch per election. */
type ElectionFilterMap = Record<string, PicksFilterValue>;

const readFromStorage = (): ElectionFilterMap => {
  try {
    // The per-level filter an earlier design used. `clearVoteMateStorage` drops
    // it on unload since it is off `PERSISTENT_KEYS`, but `beforeunload` is not
    // guaranteed to run (a discarded mobile tab, for one), so clear it here too
    // rather than leaving a dead key in storage.
    localStorage.removeItem('votemate_level_filters');
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const result: ElectionFilterMap = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (value === 'picks' || value === 'all') {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
};

/**
 * Per-election filter state (`'picks' | 'all'`) — the master switch that narrows
 * a whole ballot to the voter's hearts — persisted across refresh under the
 * `votemate_election_filters` key, paired with `useLikedCandidates` so a user's
 * "Your Picks" view is restored automatically when they return.
 *
 * `setFilter` sets rather than flips, mirroring mobile's
 * `ElectionFiltersService.set`: the header derives the value it moves to from
 * what its races show (see `masterFilterValue`).
 */
const useElectionFilters = (): {
  filters: ElectionFilterMap;
  setFilter: (electionKey: string, value: PicksFilterValue) => void;
} => {
  const [filters, setFilters] = useState<ElectionFilterMap>(readFromStorage);
  // Skip the no-op write on first render — the state was just hydrated from
  // localStorage, so persisting it back is pointless and writes `{}` every page
  // load for users who've never used the feature.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      // Store only the elections set to picks: 'all' is the default a missing
      // key already means, and mobile's store writes the same shape, so a blob
      // written by one client reads identically on the other.
      const picksOnly = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value === 'picks')
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify(picksOnly));
    } catch {
      // localStorage may be full / disabled (private mode, quota) — fail silently.
    }
  }, [filters]);

  const setFilter = useCallback((electionKey: string, value: PicksFilterValue) => {
    setFilters((prev) => {
      if ((prev[electionKey] ?? 'all') === value) return prev;
      const updated = { ...prev };
      // 'all' is the default a missing key already means.
      if (value === 'picks') updated[electionKey] = value;
      else delete updated[electionKey];
      return updated;
    });
  }, []);

  return { filters, setFilter };
};

export default useElectionFilters;
